import React, { useEffect, useMemo, useState } from 'react';
import { autofillArtistByInput, createArtist } from '@/features/artists/services/artists.api';
import { useToast } from '@/components/ToastProvider';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
}

type Autofill = {
  name: string;
  photoUrl?: string;
  followers?: number;
  genres?: string[];
  spotifyId?: string;
};

export const ArtistCreateDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState<'search'|'edit'>('search');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [input, setInput] = useState('');
  const [auto, setAuto] = useState<Autofill | null>(null);
  const toast = useToast();

  const [name, setName] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [followers, setFollowers] = useState<number>(0);
  const [genres, setGenres] = useState<string>('');
  const [spotifyId, setSpotifyId] = useState<string>('');
  const [nationality, setNationality] = useState('');
  const [careerStage, setCareerStage] = useState('');
  const [mainGenre, setMainGenre] = useState('');
  const [about, setAbout] = useState('');

  useEffect(() => {
    if (!open) {
      setStep('search'); setInput(''); setAuto(null); setError(null);
      setName(''); setPhotoUrl(''); setFollowers(0); setGenres(''); setSpotifyId('');
    }
  }, [open]);

  const chips = useMemo(() => genres.split(',').map(s=>s.trim()).filter(Boolean), [genres]);

  const handleAutofill = async () => {
    if (!input.trim()) return;
    try {
      setLoading(true); setError(null);
      const data = await autofillArtistByInput(input.trim());
      const filled: Autofill = {
        name: data.name,
        photoUrl: data.photoUrl || '',
        followers: data.followers || 0,
        genres: data.genres || [],
        spotifyId: (data as any).spotifyId || '',
      };
      setAuto(filled);
      // seed edit fields
      setName(filled.name);
      setPhotoUrl(filled.photoUrl || '');
      setFollowers(filled.followers || 0);
      setGenres((filled.genres || []).join(', '));
      setSpotifyId(filled.spotifyId || '');
      setMainGenre((filled.genres || [])[0] || '');
      setStep('edit');
    } catch (e:any) {
      const msg = e?.message || 'Falha ao buscar no Spotify';
      setError(msg);
      toast.error('Busca no Spotify', msg);
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      setLoading(true); setError(null);
      await createArtist({
        name: name.trim(),
        photoUrl: photoUrl.trim() || undefined,
        followers: Number(followers) || 0,
        listeners: 0,
        genres: genres.split(',').map(s=>s.trim()).filter(Boolean),
        spotifyId: spotifyId || undefined,
        nationality: nationality.trim() || undefined,
        careerStage: careerStage.trim() || undefined,
        mainGenre: mainGenre.trim() || undefined,
        about: about.trim() || undefined,
      } as any);
      await onCreated();
      onClose();
    } catch (e:any) {
      const msg = e?.message || 'Falha ao salvar artista';
      setError(msg);
      toast.error('Erro ao salvar', msg);
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-xl sm:rounded-2xl bg-white dark:bg-zinc-900 border border-default sm:p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-default">
          <div className="flex items-center gap-2">
            <i className="ph ph-user-plus text-xl" />
            <h2 className="text-lg font-bold">Novo artista</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 p-1 rounded-md">
            <i className="ph ph-x text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-4">
          {/* Stepper */}
          <div className="flex items-center gap-2 text-xs mb-4">
            <span className={`px-2 py-1 rounded-full border ${step==='search'?'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900':'text-muted'}`}>1</span>
            <span className="text-muted">Identificar no Spotify</span>
            <span className="mx-1 text-muted">→</span>
            <span className={`px-2 py-1 rounded-full border ${step==='edit'?'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900':'text-muted'}`}>2</span>
            <span className="text-muted">Revisar e salvar</span>
          </div>

          {step === 'search' && (
            <div className="space-y-3">
              <label className="block">
                <span className="text-sm text-muted">Cole o link do artista no Spotify ou ID</span>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder="https://open.spotify.com/artist/xxxxxxxxxxxxxxxxxxxxxx"
                    className="flex-1 px-3 py-2 rounded-md border border-default bg-transparent"
                  />
                  <button onClick={handleAutofill} disabled={loading || !input.trim()} className="px-3 py-2 rounded-md bg-zinc-900 text-white dark:bg-green-500 dark:text-black disabled:opacity-50">
                    Buscar
                  </button>
                </div>
              </label>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <p className="text-xs text-muted">Dica: aceita URLs como “open.spotify.com/artist/…” ou o ID de 22 caracteres.</p>
            </div>
          )}

          {step === 'edit' && (
          <div className="space-y-4">
              {auto && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-subtle border border-default">
                  <img src={photoUrl || 'https://placehold.co/64x64?text=A'} alt={name} className="w-16 h-16 rounded-md object-cover border border-default" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{name || '(sem nome)'}</p>
                    <p className="text-xs text-muted">{(followers||0).toLocaleString()} seguidores</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {chips.slice(0,6).map((g,i)=> (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 text-xs text-muted border border-default">{g}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm text-muted">Nome</span>
                  <input value={name} onChange={e=>setName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block">
                  <span className="text-sm text-muted">Foto (URL)</span>
                  <input value={photoUrl} onChange={e=>setPhotoUrl(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block">
                  <span className="text-sm text-muted">Seguidores</span>
                  <input type="number" value={followers} onChange={e=>setFollowers(Number(e.target.value)||0)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block">
                  <span className="text-sm text-muted">Gêneros (vírgula)</span>
                  <input value={genres} onChange={e=>setGenres(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
              </div>

              {/* Detalhes complementares */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm text-muted">Nacionalidade</span>
                  <input value={nationality} onChange={e=>setNationality(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block">
                  <span className="text-sm text-muted">Estágio de carreira</span>
                  <input value={careerStage} onChange={e=>setCareerStage(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block">
                  <span className="text-sm text-muted">Gênero principal</span>
                  <input value={mainGenre} onChange={e=>setMainGenre(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm text-muted">Sobre</span>
                  <textarea value={about} onChange={e=>setAbout(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent min-h-[80px]" />
                </label>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button onClick={()=>setStep('search')} className="px-3 py-2 rounded-md border border-default">Voltar</button>
                <div className="flex items-center gap-2">
                  <button onClick={onClose} className="px-3 py-2 rounded-md border border-default">Cancelar</button>
                  <button onClick={async()=>{ await handleSave(); if(!error){ toast.success('Artista criado'); } }} disabled={loading || !name.trim()} className="px-3 py-2 rounded-md bg-green-600 text-white disabled:opacity-50">Salvar</button>
                </div>
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtistCreateDialog;
